"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";

import { env } from "@/lib/environment";
import { cn } from "@/lib/utils";
import { NewButton, type ButtonProps } from "@/components/ui/NewButton";

type WatchVideoButtonProps = {
  label?: string;
  size?: ButtonProps["size"];
  variant?: ButtonProps["variant"];
  videoId?: string;
  videoTitle?: string;
  showPlayIcon?: boolean;
  className?: string;
};

export function WatchVideoButton({
  label = "Watch video",
  size = "lg",
  variant = "outlinePrimary",
  videoId = "9p9w7ShXZss",
  videoTitle = "Pickcel product demo",
  showPlayIcon = false,
  className,
}: WatchVideoButtonProps) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  return (
    <>
      <NewButton
        variant={variant}
        size={size}
        type="button"
        onClick={() => setOpen(true)}
        className={cn("inline-flex items-center gap-2", className)}
      >
        {label}
        {showPlayIcon ? (
          <Image
            src={`${env.NEXT_PUBLIC_CDN_URL}/images/play-button.ydKYeUm6YG4A.svg`}
            alt=""
            aria-hidden="true"
            width={24}
            height={24}
            className="shrink-0"
          />
        ) : null}
      </NewButton>

      {open ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          role="dialog"
          aria-modal="true"
          aria-label={videoTitle}
        >
          <button
            type="button"
            className="absolute inset-0 border-0 bg-black/60 p-0 cursor-default"
            aria-label="Close video"
            onClick={() => setOpen(false)}
          />
          <div
            className="relative z-10 w-full max-w-3xl overflow-hidden rounded-2xl bg-black shadow-xl"
            role="document"
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="absolute right-3 top-3 z-10 inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/70 text-white text-sm hover:bg-black"
              aria-label="Close video"
            >
              ✕
            </button>
            <div className="aspect-video w-full">
              <iframe
                src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
                title={videoTitle}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="h-full w-full"
              />
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
